/**
 * Service for FCConfigurationService API calls.
 * Provides warehouse configuration data: active FCs, container types, FC validation.
 */

import {
    FCInfo,
    GetActiveFCsByDataCenterResponse,
    GetAllContainerTypesResponse,
    IsValidFCResponse,
} from '../types/configuration';
import { getApiGatewayUrl, getWarehouseId } from '../utils/api';
import { apiFetch } from './apiClient';

/**
 * Build the complete API URL for configuration endpoints.
 * Smithy route: /{warehouseId}/Configuration/{endpoint}
 */
function getConfigurationApiUrl(endpoint: string, warehouseId?: string): string {
    const apiGatewayUrl = getApiGatewayUrl();
    const wId = warehouseId ?? getWarehouseId();
    return `${apiGatewayUrl}/${wId}/Configuration/${endpoint}`;
}

/**
 * Get all active FCs grouped by data center.
 * API Endpoint: GET /{warehouseId}/Configuration/GetActiveFCsByDataCenter
 */
export async function getActiveFCsByDataCenter(dataCenters?: string[]): Promise<Record<string, FCInfo[]>> {
    let url = getConfigurationApiUrl('GetActiveFCsByDataCenter');

    if (dataCenters && dataCenters.length > 0) {
        const params = dataCenters.map(dc => `dataCenters=${encodeURIComponent(dc)}`).join('&');
        url += '?' + params;
    }

    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        throw new Error(`GetActiveFCsByDataCenter failed: ${response.status}`);
    }

    const data: GetActiveFCsByDataCenterResponse = await response.json();
    return data.activeFCsByDataCenter;
}

/**
 * Get all container types for the current warehouse.
 * API Endpoint: GET /{warehouseId}/Configuration/GetAllContainerTypes
 */
export async function getAllContainerTypes(): Promise<string[]> {
    const url = getConfigurationApiUrl('GetAllContainerTypes');
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        throw new Error(`GetAllContainerTypes failed: ${response.status}`);
    }

    const data: GetAllContainerTypesResponse = await response.json();
    return data.containerTypes;
}

/**
 * Check if the current warehouse is a valid (active) FC.
 * API Endpoint: GET /{warehouseId}/Configuration/IsValidFC
 */
export async function isValidFC(warehouseId?: string): Promise<boolean> {
    const url = getConfigurationApiUrl('IsValidFC', warehouseId);
    const response = await apiFetch(url, { method: 'GET', credentials: 'include' });

    if (!response.ok) {
        throw new Error(`IsValidFC failed: ${response.status}`);
    }

    const data: IsValidFCResponse = await response.json();
    return data.isValid;
}
